import { z } from "zod";

/**
 * Which responsibilities a proposed task covers — the contract behind the prototype's
 * "Responsibilities it covers" checklist, whose empty state reads "Nothing
 * linked yet."
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 IDS ONLY. THE RESPONSIBILITY'S TEXT IS NOT IN THIS PAYLOAD, AND THAT IS THE WHOLE
 * DESIGN RATHER THAN AN ECONOMY.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The prototype's own model is an INDEX ARRAY — `respIdx` per task, pointing
 * into the Role's responsibility list, with one seeded task carrying `[0, 2]`. It never
 * copies the text either.
 *
 * Three things follow, and they are the reason this shape is not a convenience:
 *
 * 1. **A responsibility's text has exactly one home.** `GET /api/roles/:id/responsibilities`
 *    serves it. Returning it here as well would put the same sentence behind two
 *    reads that can disagree the moment one is cached, and the checklist would
 *    render a stale responsibility beside a fresh one with nothing on screen saying which
 *    is which.
 * 2. **The checklist already holds every responsibility before it can draw a single tick.**
 *    It renders the Role's WHOLE responsibility list with a checkbox against each, so it
 *    has made that read by construction. This payload answers only "which of the
 *    ones you are already holding are ticked".
 * 3. **It settles the capability question instead of arguing it.** A payload
 *    carrying responsibility TEXT is a `role.view` subject, so serving it under
 *    `coverage.view` would let a group holding the task tab and not the responsibilities
 *    tab read every responsibility of the Role through the checklist. That is a real leak
 *    and it is the reason a conjunction of the two capabilities was proposed.
 *    A payload of opaque ids has no such subject: it is a statement about the
 *    TASK, which is what `coverage.view` already governs, and a caller without
 *    `role.view` receives uuids that resolve to nothing they can read.
 *
 * ── ORDER IS THE RESPONSIBILITY'S OWN, AND IT IS NOT A PROPERTY OF THE LINK ───────────
 *
 * `RoleTaskResponsibility` carries no `position` column, deliberately — its own
 * model docblock states why. The read orders by the RESPONSIBILITY's `(position,
 * createdAt, id)`, which is the same total order `ListRoleResponsibilities`
 * uses, so a tick in the checklist always sits beside the responsibility it belongs to
 * whichever of the two reads arrives first.
 *
 * The prototype's chip order is its CLICK order — an artefact of `respIdx.push`
 * — and reproducing that would let one pair of responsibilities render in one order on one
 * task and the reverse on another, which reads as a bug rather than as a choice.
 */

/**
 * The responsibilities one task covers, as the API returns them.
 *
 * ORDERED BY THE RESPONSIBILITY, never by the link. See the header.
 */
export const RoleTaskResponsibilitiesViewSchema = z.strictObject({
  responsibilityIds: z.array(z.string())
});

/**
 * The whole set, stated in one request.
 *
 * ⚠️ REPLACE, NOT MERGE — and unlike the responsibilities themselves, that is the RIGHT
 * grain here. `AddRoleResponsibility` is one-per-request because a replace would
 * re-mint every responsibility's `id` and destroy the very reference this table is built
 * on. A LINK has no identity worth preserving: it exists or it does not, it
 * carries no text, no position and no id anyone points at. A checklist is
 * set-authoring — a reader ticks three boxes and expects three ticks — so one
 * request per tick is the wrong grain and would make a half-applied checklist a
 * reachable state.
 *
 * An EMPTY array is a legal, meaningful body: it unlinks every responsibility and is what
 * "untick the last box" sends. It is never confused with "no opinion", because
 * the field is required.
 *
 * 🚨 DUPLICATES ARE REFUSED RATHER THAN DEDUPED. `@@unique([taskId,
 * responsibilityId])` means the same id twice cannot be stored, so silently
 * collapsing it would answer 200 to a body the caller can never read back
 * verbatim. A client sending the same responsibility twice has a bug and is told so.
 *
 * 🚨 THE PARSE PROVES SHAPE AND NOTHING ELSE. Whether each id is a responsibility of THIS
 * Role is a question only a query can answer, and it is answered in
 * `ReplaceRoleTaskResponsibilitiesUseCase`. The schema deliberately makes no
 * such claim — the same split `RoleTasksInputSchema` states for assignments.
 */
export const RoleTaskResponsibilitiesInputSchema = z.strictObject({
  responsibilityIds: z.array(z.string()).refine((ids) => new Set(ids).size === ids.length, {
    message: "responsibilityIds must not contain the same responsibility twice"
  })
});

/**
 * The path segments of a route naming ONE task of one Role.
 *
 * 🚨 BOTH ARE `.uuid()`, AND THAT WAS ONLY EVER AVAILABLE AS ONE MOVE — the
 * identical ruling `RoleResponsibilityPathVarsSchema` records, for the identical
 * reason. `id-validation-consistency.test.ts` refuses a PARTIAL tightening: an
 * id name that is uuid-checked in one request object of a manifest must be
 * uuid-checked in all of them, and `RolePathVarsSchema` is shared by every
 * `/api/roles/:id/*` route — so `.uuid()` here alone would have flagged every
 * one of those siblings. NEX-3552 tightened them together.
 *
 * ⚠️ THE USE CASES STILL SHAPE-CHECK `taskId`, AND THAT IS A FLOOR RATHER THAN A
 * DUPLICATE. `RoleTask.id` is `@db.Uuid`, so a value that reached Prisma
 * unchecked raises `P2023` and renders as a flat 400 naming no field. Both
 * answer 404 instead, exactly as `RemoveRoleResponsibilityUseCase` already does
 * for a responsibility id, so malformed, absent and foreign stay one answer for any caller
 * that does not arrive through this schema.
 *
 * 🔴 AND `RoleIdShapeGuard` DOES NOT COVER IT. That guard reads `params.id`
 * only, so every SECOND `:<x>Id` segment on a Roles route is outside it. This is
 * not a gap in the guard — a guard that walked every param would have to know
 * which are uuids — it is a responsibility of each route that carries one.
 */
export const RoleTaskResponsibilityPathVarsSchema = z.strictObject({
  id: z.string().uuid(),
  taskId: z.string().uuid()
});

/** The responsibilities one task covers, as stored. */
export type RoleTaskResponsibilitiesView = z.infer<typeof RoleTaskResponsibilitiesViewSchema>;

/** The whole set of responsibilities a task covers, as authored. */
export type RoleTaskResponsibilitiesInput = z.infer<typeof RoleTaskResponsibilitiesInputSchema>;

/** The two ids a single-task route carries. */
export type RoleTaskResponsibilityPathVars = z.infer<typeof RoleTaskResponsibilityPathVarsSchema>;
